import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEMO } from '../lib/demo';
import { useExpedientes } from '../lib/hooks';
import type { EstadoExpediente, NivelRiesgo } from '../lib/phase1';
import { Avatar, Button, Card, EmptyState, PageHeader, Pill, Skeleton, cn, type Tone } from '../components/ui';
import { PersonaIcon } from '../components/icons';
import { FolderOpen, TriangleAlert } from 'lucide-react';

/**
 * PHASE-1 PREVIEW — client expedientes. Renders the real design system on the
 * real foundation, fed by demo data (lib/phase1.ts). Not part of Phase 0.
 */

const ESTADO_TONE: Record<EstadoExpediente, Tone> = {
  Nuevo: 'slate',
  'En Revisión': 'blue',
  'Docs Pendientes': 'amber',
  Escalado: 'red',
  Aprobado: 'green',
  Activo: 'green',
  Rechazado: 'red',
  'Actualización Requerida': 'amber',
};

const RIESGO: Record<NivelRiesgo, { tone: Tone; label: string }> = {
  bajo: { tone: 'green', label: 'Bajo' },
  medio: { tone: 'amber', label: 'Medio' },
  alto: { tone: 'red', label: 'Alto' },
  sin: { tone: 'slate', label: 'Sin evaluar' },
};

const FILTERS: { key: string; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'En Revisión', label: 'En revisión' },
  { key: 'Docs Pendientes', label: 'Docs pendientes' },
  { key: 'Escalado', label: 'Escalados' },
  { key: 'Activo', label: 'Activos' },
  { key: 'Actualización Requerida', label: 'Actualización' },
];

const initials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

export default function RevisionList() {
  const navigate = useNavigate();
  const { data: rows, isLoading, isError, refetch } = useExpedientes();
  const [filter, setFilter] = useState('all');

  const filtered = useMemo(
    () => (rows ?? []).filter((r) => filter === 'all' || r.estado === filter),
    [rows, filter]
  );

  const kpis = useMemo(() => {
    const all = rows ?? [];
    return {
      total: all.length,
      altos: all.filter((r) => r.riesgo === 'alto').length,
      revision: all.filter((r) => ['En Revisión', 'Escalado'].includes(r.estado)).length,
      vencimiento: all.filter((r) => r.estado === 'Actualización Requerida').length,
    };
  }, [rows]);

  return (
    <div>
      <PageHeader
        title="Expedientes"
        subtitle="Cola de revisión · estado de cumplimiento"
        actions={<Button onClick={() => navigate('/expedientes/nuevo')}>＋ Nuevo expediente</Button>}
      />

      <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Kpi label="Expedientes" value={kpis.total} tone="ink" />
        <Kpi label="Riesgo alto / PEP" value={kpis.altos} tone="red" />
        <Kpi label="En revisión" value={kpis.revision} tone="blue" />
        <Kpi label="Actualización requerida" value={kpis.vencimiento} tone="amber" />
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={cn(
              'rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors',
              filter === f.key
                ? 'border-side bg-side text-white'
                : 'border-line-strong bg-panel text-ink-soft hover:border-ink-faint'
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Card>
          <div className="grid gap-2.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-xl" />
            ))}
          </div>
        </Card>
      ) : isError ? (
        <Card>
          <EmptyState
            icon={<TriangleAlert className="h-8 w-8" strokeWidth={1.5} />}
            title="No se pudieron cargar los expedientes"
            hint="Verifica tu conexión e inténtalo de nuevo."
            action={<Button onClick={() => void refetch()}>Reintentar</Button>}
          />
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FolderOpen className="h-8 w-8" strokeWidth={1.5} />}
            title="Sin expedientes en este estado"
            hint="Cambia el filtro o crea un nuevo expediente para empezar el onboarding."
            action={<Button onClick={() => navigate('/expedientes/nuevo')}>＋ Nuevo expediente</Button>}
          />
        </Card>
      ) : (
        <div>
          {/* mobile: stacked cards (no horizontal scroll) */}
          <div className="grid gap-2.5 sm:hidden">
            {filtered.map((r) => {
              const risk = RIESGO[r.riesgo];
              return (
                <button
                  key={r.id}
                  onClick={() => navigate(`/revision/${r.id}`)}
                  className="w-full rounded-2xl border border-line bg-panel p-4 text-left shadow-card transition-colors hover:border-ink-faint"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10">
                      <PersonaIcon persona={r.persona} className="h-4 w-4 text-primary-dark" />
                    </span>
                    <span className="font-bold">{r.name}</span>
                  </div>
                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                    <Pill tone={ESTADO_TONE[r.estado]}>{r.estado}</Pill>
                    <Pill tone={risk.tone}>
                      {risk.label}
                      {r.pep ? ' · PEP' : ''}
                    </Pill>
                  </div>
                  <div className="mt-2 font-mono text-[11px] text-ink-faint">
                    {r.id} · {r.kind}
                  </div>
                </button>
              );
            })}
          </div>

          {/* desktop: table */}
          <Card data-tour="revision-table" className="hidden overflow-x-auto p-0 sm:block">
            <table className="w-full min-w-[46rem] text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                {['Expediente', 'Cliente', 'Tipo', 'Estado', 'Riesgo', 'Analista', 'Próx. revisión'].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-faint"
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
            {filtered.map((r) => {
              const risk = RIESGO[r.riesgo];
              return (
                <tr
                  key={r.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/revision/${r.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/revision/${r.id}`);
                    }
                  }}
                  className="cursor-pointer border-b border-line last:border-0 transition-colors hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
                >
                  <td className="px-5 py-3 font-mono text-[11px] text-ink-faint">{r.id}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10">
                        <PersonaIcon persona={r.persona} className="h-4 w-4 text-primary-dark" />
                      </span>
                      <span className="font-bold">{r.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono text-[11px] text-ink-faint">{r.kind}</td>
                  <td className="px-5 py-3">
                    <Pill tone={ESTADO_TONE[r.estado]}>{r.estado}</Pill>
                  </td>
                  <td className="px-5 py-3">
                    <Pill tone={risk.tone}>
                      {risk.label}
                      {r.pep ? ' · PEP' : ''}
                    </Pill>
                  </td>
                  <td className="px-5 py-3">
                    {r.analyst ? (
                      <span className="flex items-center gap-2">
                        <Avatar initials={initials(r.analyst)} />
                        <span className="text-ink-soft">{r.analyst}</span>
                      </span>
                    ) : (
                      <span className="text-ink-faint">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 font-mono text-[11px] text-ink-faint">
                    {r.review ?? '—'}
                  </td>
                </tr>
              );
            })}
            </tbody>
          </table>
          </Card>
        </div>
      )}

      {DEMO && (
        <p className="mt-4 font-mono text-[11px] text-ink-faint">
          Entorno demo · datos de demostración.
        </p>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'ink' | 'red' | 'blue' | 'amber';
}) {
  const color = {
    ink: 'text-ink',
    red: 'text-tone-red',
    blue: 'text-tone-blue',
    amber: 'text-tone-amber',
  }[tone];
  return (
    <Card className="p-4">
      <div className="text-xs font-semibold text-ink-soft">{label}</div>
      <div className={cn('mt-1 font-serif text-4xl font-medium', color)}>{value}</div>
    </Card>
  );
}
